import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';
import FAMOUS_PLAYERS from './mysteryPlayersData';
import CountdownTimer from './CountdownTimer';
import { buildVerifiedClues } from './mysteryClueGenerator';

const SPORTS_LIST = ['all', 'Cricket', 'Football', 'Basketball', 'Tennis', 'Boxing', 'Golf', 'Athletics', 'Swimming', 'Formula 1'];
const WIKI_API = 'https://en.wikipedia.org/w/api.php';
const PLAY_COOLDOWN_MS = 4 * 60 * 60 * 1000;

const fetchWikipediaJson = async (url, attempts = 3) => {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Wikipedia returned ${response.status}`);
      return await response.json();
    } catch (error) {
      lastError = error;
      if (attempt < attempts) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 400));
      }
    }
  }
  throw lastError;
};

const MysteryPlayerChallenge = () => {
  const [mysteryPlayer, setMysteryPlayer] = useState(null);
  const [cluesRevealed, setCluesRevealed] = useState(1);
  const [guess, setGuess] = useState('');
  const [guessHistory, setGuessHistory] = useState([]);
  const [solved, setSolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReveal, setShowReveal] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [stats, setStats] = useState({ played: 0, won: 0, streak: 0 });
  const [shareText, setShareText] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedSport, setSelectedSport] = useState('all');
  
  // Leaderboard states
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardView, setLeaderboardView] = useState('all-time');
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [submittedScore, setSubmittedScore] = useState(null);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [currentUserBest, setCurrentUserBest] = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [cooldownEndsAt, setCooldownEndsAt] = useState(null);

  const getProgressKey = useCallback((sport) => `mysteryPlayer_${sport}_progress`, []);
  const getCooldownKey = useCallback((sport) => `mysteryPlayer_${sport}_cooldown`, []);

  // Get user identifier for tracking
  const getUserIdentifier = () => {
    let identifier = localStorage.getItem('mysteryUserIdentifier');
    if (!identifier) {
      identifier = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('mysteryUserIdentifier', identifier);
    }
    return identifier;
  };

  // Fetch leaderboard data
  const fetchLeaderboard = async (view = 'all-time') => {
    setLeaderboardLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/mystery-leaderboard?view=${view}&limit=20`, {
        headers: {
          'x-user-identifier': getUserIdentifier()
        }
      });
      const data = await response.json();
      setLeaderboardData(data.leaderboard || []);
      setCurrentUserBest(data.currentUserBest);
      setTotalPlayers(data.totalPlayers || 0);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  // Submit score to leaderboard
  const submitScore = async (playerName, cluesUsed, timeSpent) => {
    if (!username.trim()) {
      setUsernameError('Please enter a username');
      return;
    }
    if (username.trim().length < 2 || username.trim().length > 30) {
      setUsernameError('Username must be 2-30 characters');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/mystery-leaderboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-identifier': getUserIdentifier()
        },
        body: JSON.stringify({
          username: username.trim(),
          playerGuessed: playerName,
          cluesUsed,
          timeSpent,
          streak: stats.streak
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setScoreSubmitted(true);
        setSubmittedScore(data.entry);
        setShowUsernameModal(false);
        localStorage.setItem('mysteryUsername', username.trim());
        fetchLeaderboard(leaderboardView);
      } else {
        setUsernameError(data.message || 'Failed to submit score');
      }
    } catch (err) {
      console.error('Error submitting score:', err);
      setUsernameError('Failed to submit score. Please try again.');
    }
  };

  // Load saved username
  useEffect(() => {
    const savedUsername = localStorage.getItem('mysteryUsername');
    if (savedUsername) {
      setUsername(savedUsername);
    }
  }, []);

  // Get daily player based on date (consistent for all users)
  const getDailyPlayer = useCallback(() => {
    const today = new Date();
    const dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
    
    // Filter by sport if selected
    let filteredPlayers = selectedSport === 'all' 
      ? FAMOUS_PLAYERS 
      : FAMOUS_PLAYERS.filter(p => p.sport === selectedSport);
    
    // Use date as seed for consistent daily player
    let seed = 0;
    for (let i = 0; i < dateString.length; i++) {
      seed += dateString.charCodeAt(i);
    }
    
    // Add sport filter to seed for different players per sport
    if (selectedSport !== 'all') {
      for (let i = 0; i < selectedSport.length; i++) {
        seed += selectedSport.charCodeAt(i);
      }
    }
    
    const index = seed % filteredPlayers.length;
    return filteredPlayers[index];
  }, [selectedSport]);

  // Load stats from localStorage
  const loadStats = useCallback(() => {
    try {
      const savedStats = localStorage.getItem('mysteryPlayerStats');
      if (savedStats) {
        setStats(JSON.parse(savedStats));
      }

      const cooldownKey = getCooldownKey(selectedSport);
      const progressKey = getProgressKey(selectedSport);
      const cooldownRaw = localStorage.getItem(cooldownKey);

      if (cooldownRaw) {
        const cooldownData = JSON.parse(cooldownRaw);
        if (cooldownData.endsAt > Date.now()) {
          setCooldownEndsAt(cooldownData.endsAt);
        } else {
          localStorage.removeItem(cooldownKey);
          localStorage.removeItem(progressKey);
          setCooldownEndsAt(null);
        }
      } else {
        setCooldownEndsAt(null);
      }

      const savedProgress = localStorage.getItem(progressKey);
      if (savedProgress) {
        const data = JSON.parse(savedProgress);
        setSolved(data.solved);
        setCluesRevealed(data.cluesRevealed || 5);
        setGuessHistory(data.guessHistory || []);
        if (data.solved && (!cooldownRaw || JSON.parse(cooldownRaw).endsAt > Date.now())) {
          setShowReveal(true);
        }
      }
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  }, [getCooldownKey, getProgressKey, selectedSport]);

  // Save progress
  const saveProgress = useCallback((isSolved, currentClues, history) => {
    localStorage.setItem(getProgressKey(selectedSport), JSON.stringify({
      solved: isSolved,
      cluesRevealed: currentClues,
      guessHistory: history
    }));
  }, [getProgressKey, selectedSport]);

  const startCooldown = useCallback(() => {
    const endsAt = Date.now() + PLAY_COOLDOWN_MS;
    localStorage.setItem(getCooldownKey(selectedSport), JSON.stringify({ endsAt }));
    setCooldownEndsAt(endsAt);
  }, [getCooldownKey, selectedSport]);

  const clearCooldownAndProgress = useCallback(() => {
    localStorage.removeItem(getCooldownKey(selectedSport));
    localStorage.removeItem(getProgressKey(selectedSport));
    setCooldownEndsAt(null);
    setSolved(false);
    setShowReveal(false);
    setCluesRevealed(1);
    setGuessHistory([]);
    setGuess('');
    setSuggestions([]);
    setShowSuggestions(false);
  }, [getCooldownKey, getProgressKey, selectedSport]);

  // Update stats
  const updateStats = (won, cluesUsed) => {
    const newStats = {
      played: stats.played + 1,
      won: won ? stats.won + 1 : stats.won,
      streak: won ? stats.streak + 1 : 0
    };
    setStats(newStats);
    localStorage.setItem('mysteryPlayerStats', JSON.stringify(newStats));
    
    // Generate share text
    const emoji = won ? '🎉' : '😔';
    const clueEmojis = Array(5).fill('⬛').map((_, i) => i < cluesUsed ? (won ? '🟢' : '🔴') : '⬜').join('');
    const text = `Mystery Player Challenge ${emoji}\n${clueEmojis}\nGuesses: ${guessHistory.length + 1}\nClues used: ${cluesUsed}/5\n\nPlay at: ${window.location.href}`;
    setShareText(text);

    // Calculate time spent and prompt for leaderboard submission if won
    if (won && gameStartTime) {
      const timeSpent = Math.floor((Date.now() - gameStartTime) / 1000);
      // Store time for later submission
      localStorage.setItem('mysteryLastGameTime', timeSpent.toString());
      localStorage.setItem('mysteryLastGameClues', cluesUsed.toString());
      localStorage.setItem('mysteryLastGamePlayer', mysteryPlayer?.name || '');
      
      // Show username modal for score submission
      setTimeout(() => {
        setShowUsernameModal(true);
      }, 1500);
    }
  };

  // Fetch player data on mount
  useEffect(() => {
    const fetchPlayerFromWiki = async (player) => {
      try {
        // The full article supplies career facts; section 0 supplies the rendered infobox.
        // Both payloads come directly from the English Wikipedia API.
        const extractUrl = `${WIKI_API}?action=query&titles=${encodeURIComponent(player.searchTerm)}&redirects=1&prop=extracts|pageimages|info&explaintext=1&exsectionformat=plain&pithumbsize=500&inprop=url&format=json&origin=*`;
        const infoboxUrl = `${WIKI_API}?action=parse&page=${encodeURIComponent(player.searchTerm)}&prop=text&section=0&formatversion=2&format=json&origin=*`;
        const [extractData, infoboxData] = await Promise.all([
          fetchWikipediaJson(extractUrl),
          fetchWikipediaJson(infoboxUrl)
        ]);
        
        const pages = extractData.query?.pages;
        if (!pages) throw new Error('Wikipedia returned no page data');
        const pageId = Object.keys(pages)[0];
        const pageData = pages[pageId];
        
        if (!pageData || pageId === '-1') {
          throw new Error('Player not found');
        }

        const extract = pageData.extract || '';
        const imageUrl = pageData.thumbnail?.source || null;
        const infoboxHtml = infoboxData.parse?.text || '';
        const wikiUrl = pageData.fullurl;

        const { cluePool, clues } = buildVerifiedClues({
          articleText: extract,
          infoboxHtml,
          player,
          wikiUrl
        });

        return {
          name: player.name,
          sport: player.sport,
          image: imageUrl,
          extract: extract,
          cluePool,
          clues,
          wikiUrl
        };
      } catch (err) {
        console.error('Error fetching player:', err);
        throw err;
      }
    };

    const fetchDailyMystery = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const player = getDailyPlayer();
        const playerData = await fetchPlayerFromWiki(player);
        setMysteryPlayer(playerData);
        setGameStartTime(Date.now()); // Track when game started
        loadStats();
      } catch (err) {
        setError('Failed to load mystery player. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDailyMystery();
  }, [getDailyPlayer, loadStats]);

  // Search for player suggestions
  const searchPlayers = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const url = `${WIKI_API}?action=opensearch&search=${encodeURIComponent(query)}&limit=8&namespace=0&format=json&origin=*`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data[1]) {
        setSuggestions(data[1].filter(name => 
          name.toLowerCase() !== mysteryPlayer?.name.toLowerCase()
        ).slice(0, 6));
      }
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  // Handle guess input
  const handleGuessChange = (e) => {
    const value = e.target.value;
    setGuess(value);
    setShowSuggestions(true);
    searchPlayers(value);
  };

  // Select suggestion
  const selectSuggestion = (suggestion) => {
    setGuess(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Submit guess
  const handleSubmitGuess = () => {
    if (!guess.trim() || solved) return;

    const normalizedGuess = guess.trim().toLowerCase();
    const normalizedAnswer = mysteryPlayer.name.toLowerCase();
    
    // Check for correct answer (allow partial matches)
    const isCorrect = normalizedAnswer.includes(normalizedGuess) || 
                      normalizedGuess.includes(normalizedAnswer) ||
                      normalizedGuess === normalizedAnswer ||
                      // Check if first and last name match
                      normalizedAnswer.split(' ').some(part => normalizedGuess.includes(part) && part.length > 3);

    const newHistory = [...guessHistory, { guess: guess.trim(), correct: isCorrect }];
    setGuessHistory(newHistory);
    setGuess('');
    setSuggestions([]);

    if (isCorrect) {
      setSolved(true);
      setShowReveal(true);
      updateStats(true, cluesRevealed);
      saveProgress(true, cluesRevealed, newHistory);
      startCooldown();
    } else {
      // Reveal next clue on wrong guess
      if (cluesRevealed < 5) {
        setCluesRevealed(prev => prev + 1);
      }
      saveProgress(false, Math.min(cluesRevealed + 1, 5), newHistory);
      
      // If all clues revealed and still wrong, show answer
      if (cluesRevealed >= 5) {
        setTimeout(() => {
          setSolved(true);
          setShowReveal(true);
          updateStats(false, 5);
          saveProgress(true, 5, newHistory);
          startCooldown();
        }, 1000);
      }
    }
  };

  // Give up
  const handleGiveUp = () => {
    setSolved(true);
    setShowReveal(true);
    setCluesRevealed(5);
    updateStats(false, cluesRevealed);
    saveProgress(true, 5, guessHistory);
    startCooldown();
  };

  // Copy share text
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Change sport filter
  const handleSportChange = (sport) => {
    setSelectedSport(sport);
    setSolved(false);
    setShowReveal(false);
    setCluesRevealed(1);
    setGuessHistory([]);
    setGuess('');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="relative">
          <div className="w-24 h-24 border-4 border-purple-200 rounded-full animate-pulse"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl animate-bounce">🕵️</span>
          </div>
        </div>
        <p className="mt-4 text-gray-600 font-medium animate-pulse">Loading Mystery Player...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-4">
        <span className="text-6xl mb-4">😢</span>
        <p className="text-red-500 font-medium mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-transparent bg-clip-text mb-2">
          🕵️ Mystery Player Challenge
        </h1>
        <p className="text-purple-100 text-sm sm:text-base font-medium">
          Guess the hidden sports legend! New player every day.
        </p>
        
        {/* Sport Filter */}
        <div className="flex flex-wrap justify-center gap-2 mt-4">
          {SPORTS_LIST.map(sport => (
            <button
              key={sport}
              onClick={() => handleSportChange(sport)}
              className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                selectedSport === sport
                  ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {sport === 'all' ? '🌟 All' : sport}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex justify-center gap-4 sm:gap-8 mb-6 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
        <div className="text-center">
          <div className="text-lg sm:text-xl font-bold text-purple-600">{stats.played}</div>
          <div className="text-xs text-gray-500">Played</div>
        </div>
        <div className="text-center">
          <div className="text-lg sm:text-xl font-bold text-green-600">{stats.won}</div>
          <div className="text-xs text-gray-500">Won</div>
        </div>
        <div className="text-center">
          <div className="text-lg sm:text-xl font-bold text-orange-500">{stats.streak}🔥</div>
          <div className="text-xs text-gray-500">Streak</div>
        </div>
        <div className="text-center">
          <div className="text-lg sm:text-xl font-bold text-blue-600">
            {stats.played > 0 ? Math.round((stats.won / stats.played) * 100) : 0}%
          </div>
          <div className="text-xs text-gray-500">Win Rate</div>
        </div>
        <button
          onClick={() => { setShowLeaderboard(true); fetchLeaderboard(leaderboardView); }}
          className="text-center px-3 py-1 rounded-lg border border-orange-300 bg-transparent hover:border-orange-400 transition-all"
        >
          <div className="text-lg sm:text-xl font-bold text-orange-500">🏆</div>
          <div className="text-xs text-orange-500 font-medium">Leaderboard</div>
        </button>
      </div>

      {/* Mystery Player Card */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Player Silhouette Section */}
        <div className="relative bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-6 sm:p-8">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-4 left-4 text-6xl opacity-30">❓</div>
            <div className="absolute bottom-4 right-4 text-6xl opacity-30">🏆</div>
          </div>
          
          <div className="relative flex flex-col items-center">
            {/* Player Image/Silhouette */}
            <div className={`relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden mb-4 transition-all duration-1000 ${
              showReveal ? 'shadow-2xl shadow-yellow-500/50' : 'shadow-xl shadow-purple-500/30'
            }`}>
              {mysteryPlayer?.image ? (
                <img
                  src={mysteryPlayer.image}
                  alt={showReveal ? mysteryPlayer.name : 'Mystery Player'}
                  className={`w-full h-full object-cover transition-all duration-1000 ${
                    showReveal 
                      ? 'filter-none scale-110' 
                      : 'filter brightness-0 contrast-100 blur-sm scale-100'
                  }`}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                  <span className="text-5xl">{showReveal ? '⭐' : '❓'}</span>
                </div>
              )}
              
              {/* Reveal Animation Overlay */}
              {showReveal && (
                <div className="absolute inset-0 bg-gradient-to-t from-yellow-500/20 to-transparent animate-pulse"></div>
              )}
            </div>

            {/* Player Name (revealed) */}
            {showReveal && (
              <div className="text-center animate-fadeIn">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                  {mysteryPlayer?.name}
                </h2>
                <div className="flex items-center justify-center gap-2">
                  <span className="px-3 py-1 bg-purple-500/30 rounded-full text-purple-200 text-sm">
                    {mysteryPlayer?.sport}
                  </span>
                </div>
              </div>
            )}

            {/* Mystery Text */}
            {!showReveal && (
              <div className="text-center">
                <p className="text-purple-300 text-lg font-medium animate-pulse">
                  Who am I? 🤔
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Clues Section */}
        <div className="p-4 sm:p-6 bg-gradient-to-b from-gray-50 to-white">
          <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center justify-between gap-2">
            <span className="flex items-center">
              <span className="mr-2">🔍</span>
              CLUES ({cluesRevealed}/5 revealed)
            </span>
            <span className="text-[10px] sm:text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1 whitespace-nowrap">
              ✓ Wikipedia verified
            </span>
          </h3>
          
          <div className="space-y-2">
            {mysteryPlayer?.clues.map((clue, index) => (
              <div
                key={index}
                className={`relative overflow-hidden rounded-lg transition-all duration-500 ${
                  index < cluesRevealed
                    ? 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200'
                    : 'bg-gray-100 border border-gray-200'
                }`}
              >
                <div className={`p-3 flex items-center gap-3 ${
                  index < cluesRevealed ? 'opacity-100' : 'opacity-50'
                }`}>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${
                    index < cluesRevealed
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-300 text-gray-500'
                  }`}>
                    {index < cluesRevealed ? clue.icon : '🔒'}
                  </span>
                  <p className={`flex-1 text-sm sm:text-base ${
                    index < cluesRevealed ? 'text-gray-800' : 'text-gray-400'
                  }`}>
                    {index < cluesRevealed ? clue.text : 'Clue locked - make a guess to unlock!'}
                  </p>
                </div>
                
                {/* Progress indicator */}
                {index < cluesRevealed && (
                  <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 animate-slideIn" 
                       style={{ width: '100%' }}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Guess Section */}
        {!solved && (
          <div className="p-4 sm:p-6 border-t border-gray-100">
            <div className="relative">
              <input
                type="text"
                value={guess}
                onChange={handleGuessChange}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmitGuess()}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Enter player name..."
                className="w-full px-4 py-3 pr-24 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all text-gray-800 placeholder-gray-400"
              />
              <button
                onClick={handleSubmitGuess}
                disabled={!guess.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
              >
                Guess
              </button>
              
              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 max-h-48 overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => selectSuggestion(suggestion)}
                      className="w-full px-4 py-3 text-left hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <span className="text-gray-800">{suggestion}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Guess History */}
            {guessHistory.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-2">Previous guesses:</p>
                <div className="flex flex-wrap gap-2">
                  {guessHistory.map((item, index) => (
                    <span
                      key={index}
                      className={`px-3 py-1 rounded-full text-sm ${
                        item.correct
                          ? 'bg-green-100 text-green-700 border border-green-300'
                          : 'bg-red-100 text-red-700 border border-red-300'
                      }`}
                    >
                      {item.guess} {item.correct ? '✓' : '✗'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Give Up Button */}
            <button
              onClick={handleGiveUp}
              className="w-full mt-4 py-2 text-gray-500 hover:text-red-500 text-sm transition-colors"
            >
              😩 Give up and reveal answer
            </button>
          </div>
        )}

        {/* Result Section */}
        {solved && (
          <div className="p-4 sm:p-6 border-t border-gray-100 bg-gradient-to-b from-white to-gray-50">
            <div className={`text-center p-4 rounded-xl mb-4 ${
              guessHistory.some(g => g.correct)
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'
                : 'bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200'
            }`}>
              <span className="text-4xl mb-2 block">
                {guessHistory.some(g => g.correct) ? '🎉' : '😔'}
              </span>
              <h3 className={`text-lg font-bold ${
                guessHistory.some(g => g.correct) ? 'text-green-700' : 'text-orange-700'
              }`}>
                {guessHistory.some(g => g.correct) 
                  ? `Correct! You got it in ${guessHistory.length} ${guessHistory.length === 1 ? 'guess' : 'guesses'}!`
                  : `The answer was ${mysteryPlayer?.name}`
                }
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Clues used: {cluesRevealed}/5
              </p>
            </div>

            {/* Share Button */}
            <button
              onClick={handleShare}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              {copied ? (
                <>✅ Copied!</>
              ) : (
                <>📤 Share Result</>
              )}
            </button>

            {/* Wikipedia Link */}
            {mysteryPlayer?.wikiUrl && (
              <a
                href={mysteryPlayer.wikiUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full mt-3 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all text-center"
              >
                📚 Learn more on Wikipedia
              </a>
            )}

            {/* View Leaderboard Button */}
            <button
              onClick={() => { setShowLeaderboard(true); fetchLeaderboard(leaderboardView); }}
              className="w-full mt-3 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              🏆 View Leaderboard
            </button>

            {/* Next Player Timer */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">
                Next mystery player in:
              </p>
              <CountdownTimer cooldownEndsAt={cooldownEndsAt} onExpire={clearCooldownAndProgress} />
            </div>
          </div>
        )}
      </div>

      {/* How to Play */}
      <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
        <h3 className="font-bold text-gray-800 mb-2 flex items-center">
          <span className="mr-2">📖</span> How to Play
        </h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• You start with 1 clue about the mystery player</li>
          <li>• Each wrong guess reveals a new clue (max 5)</li>
          <li>• Guess the player's name to win!</li>
          <li>• The fewer clues you need, the better!</li>
          <li>• New player every day at midnight</li>
        </ul>
      </div>

      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowLeaderboard(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 p-4 text-white">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <span>🏆</span> Leaderboard
                </h2>
                <button onClick={() => setShowLeaderboard(false)} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-white/80 text-sm mt-1">{totalPlayers} players worldwide</p>
              
              {/* View Tabs */}
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {['all-time', 'today', 'weekly', 'streak'].map(view => (
                  <button
                    key={view}
                    onClick={() => { setLeaderboardView(view); fetchLeaderboard(view); }}
                    className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      leaderboardView === view
                        ? 'bg-white text-orange-600'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    {view === 'all-time' ? '🌟 All Time' : view === 'today' ? '📅 Today' : view === 'weekly' ? '📆 This Week' : '🔥 Streaks'}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Leaderboard List */}
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {leaderboardLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : leaderboardData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl block mb-2">🎮</span>
                  <p>No scores yet. Be the first!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboardData.map((entry, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        entry.isCurrentUser
                          ? 'bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {/* Rank */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                        entry.rank === 2 ? 'bg-gray-300 text-gray-700' :
                        entry.rank === 3 ? 'bg-orange-400 text-orange-900' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : entry.rank}
                      </div>
                      
                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold truncate ${entry.isCurrentUser ? 'text-purple-700' : 'text-gray-800'}`}>
                          {entry.username} {entry.isCurrentUser && '(You)'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {leaderboardView === 'streak' 
                            ? `${entry.streak || entry.bestStreak || 0} day streak`
                            : `${entry.gamesWon || 1} games won`
                          }
                        </p>
                      </div>
                      
                      {/* Score */}
                      <div className="text-right">
                        <p className="font-bold text-lg text-orange-600">{entry.score}</p>
                        <p className="text-xs text-gray-500">points</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Current User (if not in top) */}
              {currentUserBest && !leaderboardData.some(e => e.isCurrentUser) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Your Best:</p>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-purple-200 text-purple-700">
                      #{currentUserBest.rank}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-purple-700">{currentUserBest.username} (You)</p>
                      <p className="text-xs text-gray-500">{currentUserBest.gamesWon} games won</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-orange-600">{currentUserBest.score}</p>
                      <p className="text-xs text-gray-500">points</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Scoring Info */}
            <div className="p-4 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                💡 Score = 1000 - (clues×100) + time bonus + streak bonus
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Username Modal for Score Submission */}
      {showUsernameModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-fadeIn">
            <div className="bg-gradient-to-r from-green-400 to-emerald-500 p-4 text-white text-center">
              <span className="text-4xl block mb-2">🎉</span>
              <h2 className="text-xl font-bold">Great Job!</h2>
              <p className="text-white/80 text-sm">Submit your score to the leaderboard</p>
            </div>
            
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setUsernameError(''); }}
                placeholder="Your name..."
                maxLength={30}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-gray-800 placeholder-gray-400"
              />
              {usernameError && (
                <p className="text-red-500 text-sm mt-2">{usernameError}</p>
              )}
              
              <div className="mt-4 p-3 bg-gray-50 rounded-xl">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Player guessed:</span>
                  <span className="font-medium text-gray-800">{mysteryPlayer?.name}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Clues used:</span>
                  <span className="font-medium text-gray-800">{cluesRevealed}/5</span>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowUsernameModal(false)}
                  className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all"
                >
                  Skip
                </button>
                <button
                  onClick={() => {
                    const timeSpent = parseInt(localStorage.getItem('mysteryLastGameTime')) || 60;
                    const clues = parseInt(localStorage.getItem('mysteryLastGameClues')) || cluesRevealed;
                    const player = localStorage.getItem('mysteryLastGamePlayer') || mysteryPlayer?.name;
                    submitScore(player, clues, timeSpent);
                  }}
                  className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  Submit Score
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Score Submitted Toast */}
      {scoreSubmitted && submittedScore && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50 animate-fadeIn">
          <p className="font-medium">🎉 Score submitted! Rank #{submittedScore.rank}</p>
          <p className="text-sm text-green-100">Score: {submittedScore.score} points</p>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { width: 0; }
          to { width: 100%; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        .animate-slideIn {
          animation: slideIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MysteryPlayerChallenge;
